import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@clerk/react';
import { useCopilotRuntimeOptional } from '@/components/agent-chat/copilot-runtime-provider.component';
import { useChatTitles, withChatTitles } from '@/hooks/chat-titles.store';
import type { ConversationSummary } from '@/hooks/use-agent-chat.hook';
import { getApiBase } from '@/lib/api-base';
import { fetchWebtokenHeadless } from '@/store/auth-helper';

/**
 * Each agent has its own chats (layout v2, section 5), as Gumloop's agents
 * have their tasks: the list, and the chat open in the dock.
 */

export interface AgentChat extends ConversationSummary {
  context_agent_id?: string | null;
}

/** Whether the agent's chats show its setup under way: a chat with anything
 *  in it. Clu no longer greets first, so the first message is the user's, and
 *  while its turn runs it is the only one saved. */
export function setupChatStarted(chats: readonly AgentChat[]): boolean {
  return chats.some((chat) => chat.message_count > 0);
}

/** Reads of agents' chats started in this task, by agents and limit. */
const agentChatReads = new Map<string, Promise<unknown>>();
/** Reads shared within one caller's scope (the sidebar's auth epoch). */
const scopedAgentChatReads = new WeakMap<
  object,
  Map<string, Promise<unknown>>
>();

/** The read open under `key` in this task and scope, or a new one. */
function sharedRead<T>(
  key: string,
  scope: object | undefined,
  start: () => Promise<T>
): Promise<T> {
  const reads = scope
    ? (scopedAgentChatReads.get(scope) ?? new Map<string, Promise<unknown>>())
    : agentChatReads;
  if (scope) scopedAgentChatReads.set(scope, reads);
  const open = reads.get(key) as Promise<T> | undefined;
  if (open) return open;
  const read = start();
  reads.set(key, read);
  const release = () => {
    if (reads.get(key) === read) reads.delete(key);
  };
  setTimeout(release, 0);
  void read.then(release, release);
  return read;
}

/**
 * One agent's chats for this user, newest first, from
 * `GET /api/v1/agent/conversations?agent_id=`, at most `limit` of them when
 * given. Unscoped, a failed read reads as no chats. With a `scope` (the
 * sidebar's auth epoch) a failed read rejects, so that surface can retain the
 * chats it already knows and offer a retry. Callers in the same task and scope
 * (the sidebar's tabs and the Chats rail as a turn ends) share one request; a
 * later call reads again.
 */
export function fetchAgentChats(
  agentId: string,
  limit?: number,
  scope?: object
): Promise<AgentChat[]> {
  return sharedRead(`${agentId}:${limit ?? ''}`, scope, () => {
    const raw = readAgentChats(agentId, limit);
    return scope ? raw : raw.catch((): AgentChat[] => []);
  });
}

/**
 * Several agents' chats for this user in one request, newest first, from
 * `GET /api/v1/agent/conversations?agent_ids=`, at most `limit` per agent,
 * each naming its agent in `context_agent_id`. Null from an API without
 * `agent_ids`: it ignores the param and returns general Clu's chats, so a
 * response that does not echo every id back in `agent_ids` is not these
 * agents' chats. A failed read rejects. Shared within `scope` as
 * fetchAgentChats is.
 */
export function fetchAgentsChats(
  agentIds: readonly string[],
  limit: number,
  scope: object
): Promise<AgentChat[] | null> {
  return sharedRead(`${agentIds.join(',')}:${limit}:batch`, scope, () =>
    readAgentsChats(agentIds, limit)
  );
}

async function readAgentsChats(
  agentIds: readonly string[],
  limit: number
): Promise<AgentChat[] | null> {
  const token = await fetchWebtokenHeadless();
  const query = `agent_ids=${agentIds.map(encodeURIComponent).join(',')}&limit=${limit}`;
  const response = await fetch(
    `${getApiBase()}/api/v1/agent/conversations?${query}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error('Could not load chats');
  const data = (await response.json()) as {
    conversations?: AgentChat[];
    agent_ids?: unknown;
  };
  const echoed = Array.isArray(data.agent_ids) ? data.agent_ids : [];
  if (!agentIds.every((id) => echoed.includes(id))) return null;
  if (!Array.isArray(data.conversations))
    throw new Error('Could not load chats');
  // Rows are grouped by the agent they name: one that names none would drop
  // out of every agent's list, so read per agent instead.
  if (data.conversations.some((chat) => !chat.context_agent_id)) return null;
  return data.conversations;
}

async function readAgentChats(
  agentId: string,
  limit?: number
): Promise<AgentChat[]> {
  const token = await fetchWebtokenHeadless();
  const query =
    `agent_id=${encodeURIComponent(agentId)}` +
    (limit ? `&limit=${limit}` : '');
  const response = await fetch(
    `${getApiBase()}/api/v1/agent/conversations?${query}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error('Could not load chats');
  const data = (await response.json()) as { conversations?: AgentChat[] };
  if (!Array.isArray(data.conversations))
    throw new Error('Could not load chats');
  return data.conversations;
}

/**
 * The agent's chats for this user, newest first, from
 * `GET /api/v1/agent/conversations?agent_id=`: read when the agent opens,
 * again after each turn and when a chat starts, and on demand. `loaded` once
 * the first read is back; failed reads retain known chats and expose Retry. A chat renamed
 * shows its new title at once.
 */
export function useAgentChatList(
  agentId: string,
  enabled: boolean
): {
  chats: AgentChat[];
  loaded: boolean;
  failed: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const { orgId, userId } = useAuth();
  const scope = JSON.stringify([orgId, userId]);
  const epoch = useRef({
    scope,
    agentId,
    enabled,
    pending: null as Promise<void> | null,
  });
  if (
    epoch.current.scope !== scope ||
    epoch.current.agentId !== agentId ||
    epoch.current.enabled !== enabled
  )
    epoch.current = { scope, agentId, enabled, pending: null };
  const origin = epoch.current;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [state, setState] = useState({
    origin,
    chats: [] as AgentChat[],
    loaded: false,
    failed: false,
    loading: false,
  });
  const refetch = useCallback((): Promise<void> => {
    if (
      !mounted.current ||
      !enabled ||
      !orgId ||
      !userId ||
      epoch.current !== origin
    )
      return Promise.resolve();
    if (origin.pending) return origin.pending;
    const active = () => mounted.current && epoch.current === origin;
    setState((previous) => ({
      origin,
      chats: previous.origin === origin ? previous.chats : [],
      loaded: previous.origin === origin && previous.loaded,
      failed: previous.origin === origin && previous.failed,
      loading: true,
    }));
    const request = (async () => {
      try {
        const chats = await readAgentChats(agentId);
        if (active())
          setState({
            origin,
            chats,
            loaded: true,
            failed: false,
            loading: false,
          });
      } catch {
        if (active())
          setState((previous) => ({
            ...previous,
            loaded: true,
            failed: true,
            loading: false,
          }));
      } finally {
        origin.pending = null;
      }
    })();
    origin.pending = request;
    return request;
  }, [agentId, enabled, origin, orgId, userId]);
  useEffect(() => {
    void refetch();
  }, [refetch]);

  const runtime = useCopilotRuntimeOptional();
  const streaming = runtime?.isStreaming ?? false;
  const conversationId = runtime?.conversationId ?? null;
  const settled = useRef({ streaming, conversationId });
  useEffect(() => {
    const before = settled.current;
    settled.current = { streaming, conversationId };
    const turnEnded = before.streaming && !streaming;
    const chatStarted =
      Boolean(conversationId) && conversationId !== before.conversationId;
    if (turnEnded || chatStarted) void refetch();
  }, [streaming, conversationId, refetch]);

  const current = state.origin === origin && enabled;
  const titles = useChatTitles();
  const chats = useMemo(
    () => (current ? withChatTitles(state.chats, titles) : []),
    [current, state.chats, titles]
  );
  return {
    chats,
    loaded: current && state.loaded,
    failed: current && state.failed,
    loading: current && state.loading,
    refetch,
  };
}

/**
 * The Agent page selects a persistent app-shell chat host. Leaving detaches
 * only the view; every host keeps its own stream, messages and context. The open chat
 * follows the URL both ways: a chat named there loads (Recents, a link, Back);
 * none is the agent's home; a send that starts a chat, or a chat cleared from
 * the dock, is written back. Opening an agent whose setup is not finished
 * reopens its latest chat, the setup conversation.
 */
export function useAgentChatSession({
  agentId,
  chatId,
  onOpenChat,
  setupUnfinished,
  chats,
  chatsLoaded,
  enabled = true,
  agentName,
}: {
  agentId: string;
  enabled?: boolean;
  agentName?: string;
  /** The chat the URL names; none is the agent's home. */
  chatId: string | undefined;
  onOpenChat: (chat: string | null, options?: { replace?: boolean }) => void;
  /** Undefined until the agent is read. */
  setupUnfinished: boolean | undefined;
  chats: readonly AgentChat[];
  chatsLoaded: boolean;
}): void {
  const runtime = useCopilotRuntimeOptional();
  const attachAgentChat = runtime?.attachAgentChat;
  const openRef = useRef(onOpenChat);
  openRef.current = onOpenChat;
  const startNewChatRef = useRef(runtime?.startNewChat);
  startNewChatRef.current = runtime?.startNewChat;
  const lastUrl = useRef<{ agentId: string; chatId?: string | null } | null>(
    null
  );
  useLayoutEffect(() => {
    if (!enabled || !attachAgentChat) return;
    const previous = lastUrl.current;
    lastUrl.current = { agentId, chatId };
    const detach = attachAgentChat({ agentId, chatId, agentName });
    // The URL dropped the open chat on the same agent (New chat, Back to the
    // agent's home): a new chat, not the remembered one put straight back.
    if (previous?.agentId === agentId && previous.chatId && !chatId)
      startNewChatRef.current?.();
    return detach;
  }, [agentId, chatId, agentName, enabled, attachAgentChat]);

  // Only the host selected for this exact URL may write it back. A previous
  // Agent's last streamed ID must never be copied into the next page's URL
  // while its host is attaching or its history is loading.
  const selected = runtime?.agentSession;
  const conversationId = runtime?.conversationId ?? null;
  useEffect(() => {
    if (
      !enabled ||
      !selected?.ready ||
      selected.agentId !== agentId ||
      selected.requestedChatId !== chatId ||
      runtime?.isLoadingConversation
    )
      return;
    if (conversationId && conversationId !== chatId)
      openRef.current(conversationId, { replace: true });
    else if (!conversationId && chatId)
      openRef.current(null, { replace: true });
  }, [
    agentId,
    chatId,
    conversationId,
    enabled,
    runtime?.isLoadingConversation,
    selected,
  ]);

  // Opening an agent mid setup reopens the setup conversation, once.
  const [checkedFor, setCheckedFor] = useState<string | null>(null);
  useEffect(() => {
    if (
      checkedFor === agentId ||
      !enabled ||
      !selected?.ready ||
      selected.agentId !== agentId
    )
      return;
    if (!chatsLoaded || setupUnfinished === undefined) return;
    setCheckedFor(agentId);
    const latest = chats[0];
    if (
      !chatId &&
      !conversationId &&
      !runtime?.isStreaming &&
      setupUnfinished &&
      latest
    )
      openRef.current(latest.id, { replace: true });
  }, [
    agentId,
    chatId,
    chats,
    chatsLoaded,
    checkedFor,
    conversationId,
    enabled,
    runtime?.isStreaming,
    selected,
    setupUnfinished,
  ]);
}
